import * as logger from 'loglevel';
import axiosWrapper from '../../helpers/axiosWrapper';
import { strFormatV2 } from '../../helpers/utils';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';

function formatValue(item) {
  // delete possible duplicated parenthesis with IataCode inside of it
  return item.name.replace(/ *\([^)]*\) */g, '');
}

export const AirportMarketFilter = {
  ALL: 'ALL',
  ORIGIN: 'ORIGIN',
  DESTINATION: 'DESTINATION',
  NONE: 'NONE'
};

export const AirportType = {
  ORIGIN: 'ORIGIN',
  DESTINATION: 'DESTINATION'
};

/**
 * Transforming data from airports api
 * @param {*} airportsRawData
 */
const transformData = (airportsRawData, labelStructure) => {
  const airports = [];
  for (let index = 0; index < airportsRawData.length; index += 1) {
    const item = airportsRawData[index];
    // TODO: if (!item.name || !item.iataCode) { Sometimes the airport API does not return field name.
    if (!item.iataCode) {
      logger.error('INTEGRATION__AIRPORT: API Response Airport Issue', item);
    } else {
      if (!item.name) {
        item.name = item.city?.name || 'NO_NAME_FROM_API';
      }

      if (item.children && Array.isArray(item.children)) {
        item.children.forEach((child) => {
          const airport = prepareAirport(child, labelStructure, item);
          airports.push(airport);
        });
      } else {
        const airport = prepareAirport(item, labelStructure);
        airports.push(airport);
      }
    }
  }
  return airports;
};

const prepareAirport = (
  item,
  labelStructure,
  parent = { iataCode: undefined, name: undefined }
) => {
  let label;
  if (labelStructure) {
    label = labelStructure ? strFormatV2(labelStructure, item) : formatValue(item);
  } else {
    label = formatValue(item);
  }

  const airport = {
    label,
    value: item.iataCode,
    search: label,
    category: true,
    ...item
  };
  if (parent.name && parent.iataCode) {
    delete airport.category;
    label = labelStructure ? strFormatV2(labelStructure, item) : formatValue(item);
    Object.assign(airport, { label });
    Object.assign(airport, { search: label });

    Object.assign(airport, { parent: `${item.name} ${item.iataCode}` });

    if (item.extraInfo && (airport.zoneId || airport.locationId)) {
      Object.assign(airport, item.extraInfo);
    }
  } else {
    //is not children element
    if (item.extraInfo) {
      Object.assign(airport, item.extraInfo);
    }
  }

  if (item.geo) {
    Object.assign(airport, { geo: item.geo });
  }
  return airport;
};

/**
 * Makes sure the airport response is complain with a basic schema
 * @param {*} airport
 */
const validateAirportResponse = (airports) => {
  const validation = { isValid: true, message: '' };
  if (!airports || !Array.isArray(airports)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: airports,
      message: 'INTEGRATION__AIRPORT: airports must be an Array'
    };
  }
  if (airports.length > 0) {
    const airport = airports[0];
    if (!airport.name || !airport.iataCode) {
      return {
        isValid: false,
        data: airport,
        message:
          'INTEGRATION__AIRPORT: Airport SCHEMA is invalid, please check Airports API documentation.'
      };
    }
  }
  return validation;
};

// const getDataWithFilter = (filter) => {
//   const filterCopy = { ...filter };
//   if (filterCopy.filterByMarket) {
//     switch (filterCopy.filterByMarket.toUpperCase()) {
//       case AirportMarketFilter.ORIGIN:
//         if (filterCopy.routeOption.airportType !== AirportType.ORIGIN) {
//           if (filterCopy.market) {
//             delete filterCopy.market;
//           }
//           if (filterCopy.filterFields) {
//             delete filterCopy.filterFields;
//           }
//         } else {
//           if (filterCopy.market) {
//             filterCopy.originMarkets = filterCopy.market;
//             delete filterCopy.market;
//           }
//         }
//         break;
//       case AirportMarketFilter.DESTINATION:
//         if (filterCopy.routeOption.airportType !== AirportType.DESTINATION) {
//           if (filterCopy.market) {
//             delete filterCopy.market;
//           }
//           if (filterCopy.filterFields) {
//             delete filterCopy.filterFields;
//           }
//         } else {
//           if (filterCopy.market) {
//             filterCopy.destinationMarkets = filterCopy.market;
//             delete filterCopy.market;
//           }
//         }
//         break;
//       default:
//     }
//     delete filterCopy.filterByMarket;
//   }
//   return filterCopy;
// };

/**
 * Retrieve airports from the airports Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getAirports = ({ labelStructure, ...filter }, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__AIRPORT: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1BreakingDealsSearch: airportApiSettings } = integrationSettings;
    const {
      cache = false,
      cacheDuration = NO_DURATION_DEFINED,
      visualizationType = 'GRID'
    } = airportApiSettings;

    const searchParams = new URLSearchParams(
      Object.entries({
        selectedOrigin: filter.routeOption.selectedOrigin,
        selectedDestination: filter.routeOption.selectedDestination,
        airportType: filter.routeOption.airportType,
        market: filter.language,
        visualization: visualizationType
      })
        .filter(([key, value]) => value != null)
        .map(([key, value]) => [key, value.toString()])
    );

    if (
      !airportApiSettings ||
      !airportApiSettings.method ||
      !airportApiSettings.endPoint ||
      !airportApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__AIRPORT: **integrationSettings.airportApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    axiosWrapper(
      {
        method: airportApiSettings.method,
        url: `${airportApiSettings.endPoint}?${searchParams.toString()}`,
        headers: { ...airportApiSettings.headers }
      },
      {
        resource: 'INTEGRATION__AIRPORT',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateAirportResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__AIRPORT: Successful request', response.data);
          const airports = transformData(response.data, labelStructure);
          resolve(airports);
        } else {
          logger.info('INTEGRATION__AIRPORT: Missing Data', validation.data);
          let newData = [];
          for (let index = 0; index < response.data.length; index++) {
            const element = response.data[index];
            if (element.name && element.iataCode) {
              newData.push(element);
            }
          }
          const airports = transformData(newData, labelStructure);
          resolve(airports);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getAirports, transformData };
